// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Workspace.js
// Desktop workspace: scanner + chart panes side by side.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import MarketScanner from './MarketScanner';
import EtfHoldingsPanel from './EtfHoldingsPanel';
import ChartPage from './Chart2';   // new chart engine (old Highcharts page: ./Chart, /chart-old)

const PANEL_MIN     = 320;
const PANEL_MAX     = 0.85;
const PANEL_DEFAULT = Math.round(window.innerWidth * 0.70);
//const PANEL_DEFAULT = 640;
const MOBILE_BP     = 768;
const SCROLL_STEP   = 120; // px per arrow click

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  root: { height:'100vh', overflow:'hidden', background:'var(--bg)', display:'flex', flexDirection:'column' },
  nav:  { background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'0 20px', height:'52px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, zIndex:20 },
  logo: { fontFamily:"'JetBrains Mono',monospace", fontSize:'12px', fontWeight:500, letterSpacing:'0.1em', color:'var(--accent)', textTransform:'uppercase' },
  navRight:  { display:'flex', alignItems:'center', gap:'12px', fontSize:'13px', color:'var(--text-muted)' },
  logoutBtn: { background:'none', border:'none', cursor:'pointer', color:'var(--accent)', fontSize:'13px', padding:'4px 10px' },
  body: { flex:1, display:'flex', overflow:'hidden', position:'relative' },

  scannerPane: (chartOpen, panelWidth, isMobile) => ({
    flex: chartOpen && !isMobile ? `0 0 calc(100% - ${panelWidth}px - 5px)` : '1 1 auto',
    overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
  }),

  resizeHandle: { width:'5px', flexShrink:0, background:'var(--border)', cursor:'col-resize', zIndex:10 },

  chartPanel: (w) => ({
    width: `${w}px`, flexShrink:0, display:'flex', flexDirection:'column',
    borderLeft:'1px solid var(--border)', background:'var(--bg)', overflow:'hidden',
  }),

  // Outer wrapper for tab strip + scroll arrows
  tabStripOuter: {
    background:'var(--surface)', borderBottom:'1px solid var(--border)',
    display:'flex', alignItems:'center',
    height:'38px', flexShrink:0, overflow:'hidden',
  },
  // Scrollable inner list of tabs — no visible scrollbar
  tabStripInner: {
    display:'flex', alignItems:'center', gap:'2px',
    overflowX:'auto', overflowY:'hidden',
    flex:1, height:'100%', padding:'0 4px',
    scrollbarWidth:'none', msOverflowStyle:'none',
  },
  // Scroll arrow buttons
  scrollArrow: {
    flexShrink:0, background:'var(--surface)', border:'none',
    borderLeft:'1px solid var(--border)', cursor:'pointer',
    color:'var(--text-muted)', fontSize:'14px',
    height:'100%', padding:'0 6px', display:'flex',
    alignItems:'center', justifyContent:'center',
    transition:'color 0.1s',
  },
  tab: (active) => ({
    display:'flex', alignItems:'center', gap:'4px',
    padding:'4px 6px 4px 6px', borderRadius:'4px 4px 0 0', cursor:'pointer',
    fontSize:'11px', fontFamily:"'JetBrains Mono',monospace",
    fontWeight: active ? 700 : 500,
    background: active ? 'var(--bg)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    border: active ? '1px solid var(--border)' : '1px solid transparent',
    borderBottom: active ? '1px solid var(--bg)' : '1px solid transparent',
    whiteSpace:'nowrap', userSelect:'none', transition:'all 0.1s', flexShrink:0,
  }),
  tabBtn: {
    background:'none', border:'none', cursor:'pointer',
    color:'var(--text-muted)', fontSize:'13px', lineHeight:1,
    padding:'1px 3px', borderRadius:'3px', display:'flex',
    alignItems:'center', justifyContent:'center',
  },
  noTabs: {
    flex:1, display:'flex', alignItems:'center', justifyContent:'center',
    color:'var(--text-muted)', fontSize:'12px', fontFamily:"'JetBrains Mono',monospace",
    flexDirection:'column', gap:'8px',
  },
  chartArea: { flex:1, overflow:'hidden', display:'flex', flexDirection:'column' },
  loadingScreen: { display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--text-muted)', fontSize:'13px' },
};

// ── TabStrip ──────────────────────────────────────────────────────────────────
// Shows ‹ › scroll arrows only when tab content overflows the strip width.
function TabStrip({ tabs, activeTab, onSelect, onRefresh, onClose }) {
  const innerRef              = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Recalculate overflow state whenever tabs change or on scroll
  const checkOverflow = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = innerRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkOverflow, { passive: true });
    // Also observe resize so arrows appear/disappear as panel is resized
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkOverflow); ro.disconnect(); };
  }, [tabs, checkOverflow]);

  const scroll = (dir) => {
    const el = innerRef.current;
    if (el) el.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <div style={S.tabStripOuter}>
      {canLeft && (
        <button style={{ ...S.scrollArrow, borderLeft:'none', borderRight:'1px solid var(--border)' }}
          onClick={() => scroll(-1)} title="Scroll left">‹</button>
      )}
      <div ref={innerRef} style={S.tabStripInner}>
        {tabs.map(t => (
          <div key={t.id} style={S.tab(t.id === activeTab)} onClick={() => onSelect(t.id)}>
            <button style={S.tabBtn} title="Refresh" onClick={(e) => { e.stopPropagation(); onRefresh(t.id); }}>↻</button>
            <span>{t.label}</span>
            <button style={S.tabBtn} title="Close"   onClick={(e) => { e.stopPropagation(); onClose(t.id); }}>×</button>
          </div>
        ))}
      </div>
      {canRight && (
        <button style={S.scrollArrow} onClick={() => scroll(1)} title="Scroll right">›</button>
      )}
    </div>
  );
}

// ── ChartContent ──────────────────────────────────────────────────────────────
function ChartContent({ tabs, activeTab, onSelect, onRefresh, onClose, onTickerChange }) {
  return (
    <div style={S.chartArea}>
      <TabStrip tabs={tabs} activeTab={activeTab} onSelect={onSelect} onRefresh={onRefresh} onClose={onClose} />
      {tabs.length === 0 ? (
        <div style={S.noTabs}>
          <span style={{ fontSize:'28px' }}>📈</span>
          <span>Click a ticker to open a chart</span>
        </div>
      ) : (
        tabs.map(t => (
          <div
            key={t.id}
            style={{
              flex:1, overflow:'hidden', flexDirection:'column',
              display: 'flex',
              visibility: t.id === activeTab ? 'visible' : 'hidden',
              position: t.id === activeTab ? 'relative' : 'absolute',
            }}
          >
            <ChartPage
              embedded
              initialTicker={t.initialTicker}
              refreshKey={t.refreshKey}
              onTickerChange={(newTicker) => onTickerChange(t.id, newTicker)}
            />
          </div>
        ))
      )}
    </div>
  );
}

// ── Workspace ─────────────────────────────────────────────────────────────────
export default function Workspace({ etf = false }) {
  const { user, loading } = useAuth();
  const navigate                  = useNavigate();
  const query = new URLSearchParams(window.location.search);
  const etfSymbol = etf ? (query.get('symbol') || '').trim().toUpperCase() : '';
  const etfExchange = etf ? (query.get('exchange') || 'AU').trim().toUpperCase() : '';
  const rootTicker = etfSymbol ? `${etfSymbol}:${etfExchange}` : '';

  const [tabs,       setTabs]       = useState(() => rootTicker ? [{ id:1, label:rootTicker, initialTicker:rootTicker, refreshKey:0 }] : []);
  const [activeTab,  setActiveTab]  = useState(rootTicker ? 1 : null);
  const [panelWidth, setPanelWidth] = useState(() => Math.round(window.innerWidth * 0.70));
//  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth < MOBILE_BP);
  const tabIdRef   = useRef(rootTicker ? 1 : 0);
  const dragging   = useRef(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(0);
  const tabTickers = useRef(rootTicker ? { 1: rootTicker } : {});
  const resizeWidthRef = useRef(PANEL_DEFAULT);

  // Set page title
  useEffect(() => { document.title = etf ? `${rootTicker || 'ETF'} Holdings | chart.ninja` : 'Workspace | chart.ninja'; }, [etf, rootTicker]);

  // Auth guard
  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  // Mobile redirect — send to standalone scanner
  useEffect(() => {
    if (isMobile && !etf) navigate('/scanner', { replace: true });
  }, [isMobile, navigate, etf]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleTickerClick = useCallback((ticker) => {
    setTabs(prev => {
      const existing = prev.find(t => tabTickers.current[t.id] === ticker || t.initialTicker === ticker);
      if (existing) { setActiveTab(existing.id); return prev; }
      const id = ++tabIdRef.current;
      tabTickers.current[id] = ticker;
      setActiveTab(id);
      return [...prev, { id, label: ticker, initialTicker: ticker, refreshKey: 0 }];
    });
  }, []);

  const handleSelect  = useCallback((id) => setActiveTab(id), []);

  const handleRefresh = useCallback((id) => {
    setTabs(prev => prev.map(t => t.id === id ? { ...t, refreshKey: t.refreshKey + 1 } : t));
  }, []);

  const handleClose = useCallback((id) => {
    delete tabTickers.current[id];
    setTabs(prev => {
      const next = prev.filter(t => t.id !== id);
      setActiveTab(cur => {
        if (cur !== id) return cur;
        const idx = prev.findIndex(t => t.id === id);
        return next[Math.max(0, idx - 1)]?.id || next[0]?.id || null;
      });
      return next;
    });
  }, []);

  const handleTickerChange = useCallback((tabId, newTicker) => {
    setTabs(prev => {
      const existing = prev.find(t => t.id !== tabId && (tabTickers.current[t.id] === newTicker || t.initialTicker === newTicker));
      if (existing) { setActiveTab(existing.id); return prev; }
      tabTickers.current[tabId] = newTicker;
      return prev.map(t => t.id === tabId ? { ...t, label: newTicker } : t);
    });
  }, []);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current   = true;
    dragStartX.current = e.clientX;
    dragStartW.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const delta = dragStartX.current - e.clientX;
      const maxW  = window.innerWidth * PANEL_MAX;
      resizeWidthRef.current = Math.min(maxW, Math.max(PANEL_MIN, dragStartW.current + delta));
      const panel = document.getElementById('ws-chart-panel');
      if (panel) panel.style.width = resizeWidthRef.current + 'px';
      const sp = document.getElementById('ws-scanner-pane');
      if (sp) sp.style.flex = `0 0 calc(100% - ${resizeWidthRef.current}px - 5px)`;
    };
    const onMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setPanelWidth(resizeWidthRef.current);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);


  if (loading || !user) return <div style={S.loadingScreen}>Loading...</div>;

  // Don't render workspace layout on mobile — redirect already fired above
  if (isMobile && !etf) return null;

  const chartOpen = tabs.length > 0;

  return (
    <div style={S.root}>
      <Navbar rightLinks={etf ? [{ label:'ETF list', href:'/etf', muted:true }] : [{ label: 'Mobile view', href: '/scanner', muted: true }]} />

      <div style={{...S.body, flexDirection:etf && isMobile?'column':'row'}}>

       <div id="ws-scanner-pane" style={{...S.scannerPane(chartOpen, panelWidth, isMobile), ...(etf && isMobile ? {flex:"0 0 42%",borderBottom:"1px solid var(--border)"} : {})}}>
         {etf ? <EtfHoldingsPanel symbol={etfSymbol} exchange={etfExchange} openChart={handleTickerClick} /> : <MarketScanner embedded onTickerClick={handleTickerClick} />}
       </div>

        {chartOpen && (
          <>
            {!(etf && isMobile) && <div style={S.resizeHandle} onMouseDown={onResizeMouseDown} title="Drag to resize" />}
            <div id="ws-chart-panel" style={{...S.chartPanel(panelWidth), ...(etf && isMobile ? {width:"100%", flex:"1 1 58%",minHeight:0} : {})}}>
              <ChartContent
                tabs={tabs}
                activeTab={activeTab}
                onSelect={handleSelect}
                onRefresh={handleRefresh}
                onClose={handleClose}
                onTickerChange={handleTickerChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
